import { motion } from "framer-motion";

/**
 * Animated phone with payment UI — replaces payment_app_close_up photo.
 */
export default function PaymentIllustration() {
    return (
        <div className="relative w-full aspect-square sm:aspect-[5/6] rounded-3xl overflow-hidden bg-gradient-to-br from-[#10B981] via-[#059669] to-[#0A192F]">
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -top-32 -right-32 h-96 w-96 rounded-full"
                style={{
                    background: "conic-gradient(from 0deg, #34D399, transparent 40%, #10B981, transparent 80%)",
                    opacity: 0.3,
                }}
            />

            {/* Floating phone */}
            <motion.div
                initial={{ y: 30, opacity: 0, rotate: -3 }}
                animate={{
                    y: [0, -10, 0],
                    opacity: 1,
                    rotate: [-3, 3, -3]
                }}
                transition={{
                    y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" },
                    opacity: { duration: 0.7 },
                }}
                className="absolute left-1/2 -translate-x-1/2 top-[8%] w-[55%] max-w-[240px]"
            >
                <div className="bg-slate-900 rounded-[2.5rem] p-2 shadow-2xl shadow-black/40 border-2 border-slate-700">
                    {/* Notch */}
                    <div className="h-3 w-16 bg-slate-900 rounded-full mx-auto mb-1" />
                    {/* Screen */}
                    <div className="rounded-[2rem] bg-gradient-to-b from-white to-slate-50 p-3 aspect-[9/19] overflow-hidden">
                        {/* Status bar */}
                        <div className="flex justify-between items-center text-[6px] font-bold text-slate-800 mb-2">
                            <span>09:41</span>
                            <div className="flex gap-1">
                                <div className="h-1.5 w-3 rounded-sm bg-slate-800" />
                                <div className="h-1.5 w-2 rounded-sm bg-slate-800" />
                            </div>
                        </div>
                        {/* App content */}
                        <div className="text-center">
                            <div className="text-[6px] text-slate-500 uppercase tracking-widest font-semibold">Bayar QRIS</div>
                            <div className="text-[10px] font-heading font-extrabold text-brand-deep mt-1">Rp 50.000</div>
                            <div className="text-[6px] text-slate-500 mt-0.5">ke Warung Berkah</div>
                        </div>
                        {/* QR placeholder */}
                        <div className="mx-auto mt-2 grid grid-cols-7 gap-0.5 w-16 h-16">
                            {Array.from({ length: 49 }).map((_, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.02, delay: 0.3 + i * 0.01 }}
                                    className={Math.random() > 0.5 ? "bg-slate-900" : "bg-transparent"}
                                />
                            ))}
                        </div>
                        {/* Pay button */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{
                                opacity: 1,
                                boxShadow: ["0 0 0 0 rgba(16,185,129,0.4)", "0 0 0 8px rgba(16,185,129,0)"]
                            }}
                            transition={{
                                opacity: { delay: 0.8 },
                                boxShadow: { duration: 2, repeat: Infinity }
                            }}
                            className="bg-brand-green text-white rounded-full py-1.5 text-[7px] font-bold text-center mt-2"
                        >
                            ✓ Bayar Sekarang
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            {/* Floating bubbles - product categories */}
            {[
                { emoji: "📱", x: "5%", y: "15%", delay: 0.5 },
                { emoji: "⚡", x: "75%", y: "20%", delay: 0.8 },
                { emoji: "🎮", x: "10%", y: "55%", delay: 1.1 },
                { emoji: "🏥", x: "78%", y: "60%", delay: 1.4 },
                { emoji: "💳", x: "8%", y: "78%", delay: 1.7 },
                { emoji: "💧", x: "76%", y: "82%", delay: 2.0 },
            ].map((b, i) => (
                <motion.div
                    key={i}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                        scale: 1,
                        opacity: 1,
                        y: [0, -10, 0]
                    }}
                    transition={{
                        scale: { duration: 0.5, delay: b.delay, type: "spring" },
                        opacity: { duration: 0.5, delay: b.delay },
                        y: { duration: 3 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: b.delay + 1 }
                    }}
                    className="absolute h-12 w-12 rounded-2xl bg-white/95 backdrop-blur-md shadow-xl flex items-center justify-center text-xl"
                    style={{ left: b.x, top: b.y }}
                >
                    {b.emoji}
                </motion.div>
            ))}

            {/* Connecting line animation */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path
                    d="M 12 22 Q 50 30 80 27"
                    stroke="white"
                    strokeWidth="0.3"
                    strokeDasharray="2 2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 1.5 }}
                />
            </svg>
        </div>
    );
}
